import React from 'react';
import { ClipboardList, Activity, Dumbbell, LineChart, Package, Check } from 'lucide-react';
import { Ring, WaveChart } from './charts';

import { localISODate } from '../lib/utils';
export const STAGES = [
  { key: 'ocena_funkcjonalna', label: 'Ocena funkcjonalna', description: 'Analiza obecnej kondycji i możliwości klienta' },
  { key: 'identyfikacja_przeszkod', label: 'Identyfikacja przeszkód', description: 'Określenie barier i wyzwań do pokonania' },
  { key: 'plan', label: 'Plan', description: 'Stworzenie spersonalizowanego planu treningowego' },
  { key: 'monitoring_korekty', label: 'Monitoring i korekty', description: 'Śledzenie postępów i dostosowanie podejścia' },
  { key: 'utrzymanie_efektow', label: 'Utrzymanie efektów', description: 'Długoterminowe utrzymanie osiągniętych rezultatów' },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }) : null);

export const ActionTiles = ({ id, navigate, client, fms, activePlan, lastEntry, pkg }) => {
  const hi = client.health_interview;
  const tiles = [
    { key: 'health-interview', t: 'Wywiad', s: hi?.completed ? `Uzupełniony ${fmtDate(hi.completed_at) || ''}` : 'Do uzupełnienia', to: `/client/${id}/health-interview`, icon: ClipboardList, todo: !hi?.completed },
    { key: 'fms', t: 'Ocena FMS', s: fms ? `${fms.total_score} / ${fms.tests.length * 3} · ${fmtDate(fms.assessment_date)}` : 'Brak oceny', to: `/client/${id}/fms`, icon: Activity, todo: !fms },
    { key: 'plan-builder', t: 'Plan', s: activePlan ? `${activePlan.plan_name} · ${activePlan.frequency}× w tyg.` : 'Nowy plan', to: activePlan ? `/client/${id}/plan-builder/${activePlan.id}` : `/client/${id}/plan-builder`, icon: Dumbbell, todo: !activePlan },
    { key: 'monitoring', t: 'Monitoring', s: lastEntry ? `Ostatni wpis: ${fmtDate(lastEntry.log_date)}` : 'Brak wpisów', to: `/client/${id}/monitoring`, icon: LineChart, todo: false },
    { key: 'packages', t: 'Pakiety', s: pkg ? (pkg.sessions_total != null ? `${pkg.sessions_used} / ${pkg.sessions_total} sesji` : pkg.package_label) : 'Brak pakietu', to: `/client/${id}/packages`, icon: Package, todo: !pkg },
  ];
  return (
    <section aria-label="Akcje klienta" className="grid grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4">
      {tiles.map(({ key, t, s, to, icon: Icon, todo }) => (
        <button key={key} onClick={() => navigate(to)}
          className="glass rounded-[22px] p-3.5 sm:p-5 min-h-[96px] sm:min-h-[148px] flex flex-col gap-3 text-left hover:border-[#C9A84C]/50 transition-colors"
          data-testid={`${key}-nav-button`}>
          <span className="flex justify-between items-start w-full">
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[var(--track)] flex items-center justify-center"><Icon className="w-4 h-4" strokeWidth={1.7} /></span>
            <span className={`w-2 h-2 rounded-full ${todo ? 'bg-[#C9A84C] shadow-[0_0_10px_rgba(201,168,76,0.8)]' : 'bg-[var(--text-primary)]'}`} />
          </span>
          <span className="mt-auto text-[13px] sm:text-base font-bold">{t}</span>
          <span className="hidden sm:block text-xs text-[var(--text-muted)] -mt-2 line-clamp-2">{s}</span>
        </button>
      ))}
    </section>
  );
};

export const StageTimeline = ({ client, activeStage, setActiveStage }) => (
  <ol className="flex flex-col">
    {STAGES.map((stage, i) => {
      const done = client.stages[stage.key]?.status === 'completed';
      const active = i === activeStage;
      return (
        <li key={stage.key} className="flex gap-4">
          <div className="flex flex-col items-center w-[30px] shrink-0">
            <span className={`w-[30px] h-[30px] rounded-full flex items-center justify-center text-xs font-extrabold shrink-0 ${
              done ? 'bg-[var(--cta-bg)] text-[var(--cta-fg)]' : active ? 'border-[1.5px] border-[#C9A84C] text-[#C9A84C] shadow-[0_0_14px_rgba(201,168,76,0.45)]' : 'border border-[var(--glass-soft-border)] text-[var(--text-muted)]'}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            {i < STAGES.length - 1 && <span className={`w-[2px] flex-1 min-h-[18px] rounded ${done ? 'bg-[var(--text-muted)]' : 'bg-[var(--track)]'}`} />}
          </div>
          <button onClick={() => setActiveStage(i)} data-testid={`stage-${i}`}
            className={`flex-1 min-w-0 text-left mb-3 flex flex-col gap-1 ${active ? 'p-3.5 rounded-2xl border border-[#C9A84C]/40 bg-[#C9A84C]/[0.07]' : 'pt-1 pb-3'}`}>
            <span className="flex justify-between gap-2">
              <span className="font-bold text-[15px]">{stage.label}</span>
              {done && client.stages[stage.key]?.completed_at && <span className="text-[11px] text-[var(--text-muted)]">{fmtDate(client.stages[stage.key].completed_at)}</span>}
            </span>
            <span className="hidden sm:block text-xs text-[var(--text-muted)] leading-relaxed">{stage.description}</span>
          </button>
        </li>
      );
    })}
  </ol>
);

export const StageMetrics = ({ monitoring, fms, workoutLogs }) => {
  const recent = [...monitoring].sort((a, b) => a.log_date.localeCompare(b.log_date));
  const pains = recent.filter((m) => m.pain_level != null);
  const last7 = pains.slice(-7);
  const avgPain = last7.length ? last7.reduce((s, m) => s + m.pain_level, 0) / last7.length : null;
  const since = localISODate(new Date(Date.now() - 28 * 864e5));
  const workouts28 = workoutLogs.filter((l) => l.log_date >= since).length;
  const loads = [...workoutLogs].sort((a, b) => a.log_date.localeCompare(b.log_date)).map((l) => {
    const w = l.exercises.map((e) => e.actual_weight).filter((v) => v != null && v > 0);
    return w.length ? w.reduce((s, v) => s + v, 0) / w.length : null;
  }).filter((v) => v != null).slice(-10);
  const fmsMax = fms ? fms.tests.length * 3 : 21;

  return (
    <>
      <div className="grid grid-cols-3 gap-2 sm:gap-3.5">
        {[
          { l: 'Ból · 7 dni', v: avgPain != null ? avgPain.toFixed(1).replace('.', ',') : '—', frac: avgPain != null ? avgPain / 10 : 0, s: pains.length ? `pierwszy wpis: ${pains[0].pain_level}` : 'brak wpisów' },
          { l: 'FMS', v: fms ? fms.total_score : '—', frac: fms ? fms.total_score / fmsMax : 0, s: fms ? `z ${fmsMax}` : 'brak oceny' },
          { l: 'Treningi', v: workouts28, frac: Math.min(1, workouts28 / 12), s: 'w 4 tygodnie', gold: true },
        ].map((g) => (
          <div key={g.l} className="glass-soft rounded-[22px] p-3 sm:p-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <Ring value={g.frac} size={68} stroke={11} gold={g.gold}><span className="big-num text-base">{g.v}</span></Ring>
            <div className="text-center sm:text-left">
              <p className="label-text text-[var(--text-muted)] text-[0.55rem] tracking-[0.18em]">{g.l}</p>
              <p className="hidden sm:block text-xs text-[var(--text-muted)] mt-1.5">{g.s}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <p className="label-text text-[var(--text-muted)]">Obciążenie i ból</p>
          <div className="flex gap-4 text-xs text-[var(--text-muted)]">
            <span className="flex items-center gap-2"><span className="w-4 h-[2px] bg-[#C9A84C]" />Śr. ciężar (kg)</span>
            <span className="flex items-center gap-2"><span className="w-4 h-[2px] bg-[var(--chart-main)]" />Ból</span>
          </div>
        </div>
        {loads.length >= 2 && <WaveChart id="load" values={loads} gold height={120} label="Średni ciężar roboczy w kolejnych treningach" />}
        {pains.length >= 2 && <WaveChart id="pain" values={pains.slice(-10).map((m) => m.pain_level)} min={0} max={10} dashed height={90} label="Poziom bólu w kolejnych wpisach" />}
        {loads.length < 2 && pains.length < 2 && <p className="text-sm text-[var(--text-muted)]">Wykres pojawi się po kilku treningach i wpisach.</p>}
      </div>
    </>
  );
};
